import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Plus, Trash2, FlaskConical } from 'lucide-react';
import { SectionCard } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { api } from '../api';

interface VariantDraft {
  id?: string;
  title: string;
  sku: string;
  barcode: string;
  price: string;
  weight: string;
  weight_unit: string;
  inventory_quantity: string;
}

interface ImageDraft {
  id?: string;
  image_url: string;
  alt_text: string;
  sort_order: number;
}

const CATEGORIES = ['general', 'apparel', 'accessories', 'beauty', 'home', 'electronics', 'food', 'sports', 'toys', 'other'];
const STATUSES = ['draft', 'active', 'paused', 'archived'];

function emptyVariant(): VariantDraft {
  return { title: '', sku: '', barcode: '', price: '', weight: '', weight_unit: 'kg', inventory_quantity: '0' };
}

export default function AdminProductForm() {
  const { t } = useTranslation(['adminProducts', 'products']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [vendorId, setVendorId] = useState<number | ''>('');

  const [title, setTitle] = useState('');
  const [titleZh, setTitleZh] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionZh, setDescriptionZh] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [moq, setMoq] = useState('1');
  const [leadTime, setLeadTime] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [status, setStatus] = useState('draft');

  const [variants, setVariants] = useState<VariantDraft[]>([emptyVariant()]);
  const [deletedVariantIds, setDeletedVariantIds] = useState<string[]>([]);
  const [images, setImages] = useState<ImageDraft[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    api.getVendors().then((r: any) => setVendors((r.vendors || []).map((v: any) => ({ id: v.id, name: v.name })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const result = await api.getAdminVendorProduct(id);
        const p = result.data;
        if (!p) { addToast('Product not found', 'error'); navigate('/admin/products'); return; }
        setVendorId(p.vendor_id);
        setTitle(p.title || '');
        setTitleZh(p.title_zh || '');
        setDescription(p.description || '');
        setDescriptionZh(p.description_zh || '');
        setCategory(p.category || 'general');
        setTags((p.tags || []).join(', '));
        setPriceMin(p.price_min != null ? String(p.price_min) : '');
        setPriceMax(p.price_max != null ? String(p.price_max) : '');
        setMoq(p.moq != null ? String(p.moq) : '1');
        setLeadTime(p.lead_time_days != null ? String(p.lead_time_days) : '');
        setIsTest(p.is_test ?? false);
        setStatus(p.status || 'draft');
        setVariants(
          p.variants?.length
            ? p.variants.map((v: any) => ({
                id: v.id,
                title: v.title || '',
                sku: v.sku || '',
                barcode: v.barcode || '',
                price: v.price != null ? String(v.price) : '',
                weight: v.weight != null ? String(v.weight) : '',
                weight_unit: v.weight_unit || 'kg',
                inventory_quantity: v.inventory_quantity != null ? String(v.inventory_quantity) : '0',
              }))
            : [emptyVariant()]
        );
        setImages(
          (p.images || []).map((img: any) => ({
            id: img.id,
            image_url: img.image_url,
            alt_text: img.alt_text || '',
            sort_order: img.sort_order || 0,
          }))
        );
      } catch {
        addToast('Failed to load product', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const updateVariant = (index: number, key: keyof VariantDraft, value: string) => {
    const next = [...variants];
    (next[index] as any)[key] = value;
    setVariants(next);
  };

  const addVariant = () => setVariants([...variants, emptyVariant()]);

  const removeVariant = (index: number) => {
    const v = variants[index];
    if (v.id) setDeletedVariantIds(prev => [...prev, v.id!]);
    setVariants(variants.filter((_, i) => i !== index));
  };

  const addImage = () => {
    if (!newImageUrl.trim()) return;
    setImages([...images, { image_url: newImageUrl.trim(), alt_text: '', sort_order: images.length }]);
    setNewImageUrl('');
  };

  const removeImage = (index: number) => {
    const img = images[index];
    if (img.id) setDeletedImageIds(prev => [...prev, img.id!]);
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!vendorId) { addToast(t('form.vendorRequired'), 'error'); return; }
    if (!title.trim()) { addToast('Title is required', 'error'); return; }

    setSaving(true);
    try {
      const tagArr = tags.split(',').map(t => t.trim()).filter(Boolean);
      const variantData = variants.filter(v => v.title || v.sku || v.price).map(v => ({
        ...(v.id ? { id: v.id } : {}),
        title: v.title || undefined,
        sku: v.sku || undefined,
        barcode: v.barcode || undefined,
        price: v.price ? parseFloat(v.price) : undefined,
        weight: v.weight ? parseFloat(v.weight) : undefined,
        weight_unit: v.weight_unit || 'kg',
        inventory_quantity: parseInt(v.inventory_quantity) || 0,
      }));

      if (isEdit && id) {
        await api.updateAdminVendorProduct(id, {
          title, title_zh: titleZh || undefined,
          description: description || undefined, description_zh: descriptionZh || undefined,
          category, tags: tagArr,
          price_min: priceMin ? parseFloat(priceMin) : undefined,
          price_max: priceMax ? parseFloat(priceMax) : undefined,
          moq: moq ? parseInt(moq) : undefined,
          lead_time_days: leadTime ? parseInt(leadTime) : undefined,
          is_test: isTest, status,
          variants: variantData,
          images: images.filter(img => !img.id),
          deleted_variant_ids: deletedVariantIds,
          deleted_image_ids: deletedImageIds,
        });
        addToast(t('form.updateSuccess'), 'success');
      } else {
        await api.createAdminVendorProduct({
          vendor_id: vendorId,
          title, title_zh: titleZh || undefined,
          description: description || undefined, description_zh: descriptionZh || undefined,
          category, tags: tagArr,
          price_min: priceMin ? parseFloat(priceMin) : undefined,
          price_max: priceMax ? parseFloat(priceMax) : undefined,
          moq: moq ? parseInt(moq) : undefined,
          lead_time_days: leadTime ? parseInt(leadTime) : undefined,
          is_test: isTest, status,
          variants: variantData,
          images: images.map(img => ({ image_url: img.image_url, alt_text: img.alt_text, sort_order: img.sort_order })),
        });
        addToast(t('form.createSuccess'), 'success');
      }
      setTimeout(() => navigate('/admin/products'), 500);
    } catch {
      addToast(t('form.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-white/5 rounded" />
          <div className="h-64 bg-white/5 rounded" />
        </div>
      </div>
    );
  }

  const inputStyle = { border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.03)' };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/admin/products')} className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 mb-3 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('detail.backToProducts')}
          </button>
          <h1 className="text-xl font-display font-bold text-white">
            {isEdit ? t('form.editTitle') : t('form.createTitle')}
          </h1>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-1.5 text-sm">
          <Save className="w-3.5 h-3.5" /> {saving ? t('form.saving') : t('form.save')}
        </button>
      </div>

      {/* Vendor selector */}
      {!isEdit && (
        <SectionCard title={t('form.vendor')}>
          <div className="p-5">
            <select
              value={vendorId}
              onChange={e => setVendorId(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full py-2 px-3 text-sm text-white/80 bg-transparent outline-none"
              style={inputStyle}
            >
              <option value="">{t('form.selectVendor')}</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </SectionCard>
      )}

      {/* Basic Info */}
      <SectionCard title={t('form.basicInfo')}>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.title')}</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="input w-full" placeholder="Product title" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.titleZh')}</label>
              <input value={titleZh} onChange={e => setTitleZh(e.target.value)} className="input w-full" placeholder="产品标题" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.description')}</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} className="input w-full min-h-[60px]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.descriptionZh')}</label>
              <textarea value={descriptionZh} onChange={e => setDescriptionZh(e.target.value)} className="input w-full min-h-[60px]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.category')}</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="input w-full" style={inputStyle}>
                {CATEGORIES.map(c => <option key={c} value={c}>{t(`products:categories.${c}`)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.tags')}</label>
              <input value={tags} onChange={e => setTags(e.target.value)} className="input w-full" placeholder={t('form.tagsHint')} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-white/40 uppercase">{t('form.status')}</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="input w-full" style={inputStyle}>
                {STATUSES.map(s => <option key={s} value={s}>{t(`products:status.${s}`)}</option>)}
              </select>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Pricing */}
      <SectionCard title={t('form.pricing')}>
        <div className="p-5 grid grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs text-white/40 uppercase">{t('form.priceMin')}</label>
            <input type="number" step="0.01" value={priceMin} onChange={e => setPriceMin(e.target.value)} className="input w-full" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-white/40 uppercase">{t('form.priceMax')}</label>
            <input type="number" step="0.01" value={priceMax} onChange={e => setPriceMax(e.target.value)} className="input w-full" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-white/40 uppercase">{t('form.moq')}</label>
            <input type="number" value={moq} onChange={e => setMoq(e.target.value)} className="input w-full" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-white/40 uppercase">{t('form.leadTime')}</label>
            <input type="number" value={leadTime} onChange={e => setLeadTime(e.target.value)} className="input w-full" />
          </div>
        </div>
        <div className="px-5 pb-5">
          <label className="flex items-center gap-2 text-sm text-white/50 cursor-pointer">
            <input type="checkbox" checked={isTest} onChange={e => setIsTest(e.target.checked)} className="accent-[#FFD400]" />
            <FlaskConical className="w-3.5 h-3.5" />
            {t('form.isTest')}
            <span className="text-xs text-white/25 ml-1">{t('form.isTestHint')}</span>
          </label>
        </div>
      </SectionCard>

      {/* Variants */}
      <SectionCard title={t('form.variants')}>
        <div className="p-5 space-y-3">
          {variants.map((v, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1 grid grid-cols-5 gap-2">
                <input value={v.sku} onChange={e => updateVariant(i, 'sku', e.target.value)} className="input text-xs" placeholder={t('form.sku')} />
                <input value={v.title} onChange={e => updateVariant(i, 'title', e.target.value)} className="input text-xs" placeholder={t('form.variantTitle')} />
                <input type="number" step="0.01" value={v.price} onChange={e => updateVariant(i, 'price', e.target.value)} className="input text-xs" placeholder={t('form.price')} />
                <input type="number" step="0.01" value={v.weight} onChange={e => updateVariant(i, 'weight', e.target.value)} className="input text-xs" placeholder={t('form.weight')} />
                <input type="number" value={v.inventory_quantity} onChange={e => updateVariant(i, 'inventory_quantity', e.target.value)} className="input text-xs" placeholder={t('form.inventoryQty')} />
              </div>
              <button onClick={() => removeVariant(i)} className="p-1.5 text-white/20 hover:text-[#f87171] transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={addVariant} className="btn-ghost text-xs flex items-center gap-1">
            <Plus className="w-3 h-3" /> {t('form.addVariant')}
          </button>
        </div>
      </SectionCard>

      {/* Images */}
      <SectionCard title={t('form.images')}>
        <div className="p-5 space-y-3">
          {images.map((img, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded overflow-hidden bg-white/5 shrink-0">
                <img src={img.image_url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <span className="text-xs text-white/40 flex-1 truncate">{img.image_url}</span>
              <button onClick={() => removeImage(i)} className="p-1.5 text-white/20 hover:text-[#f87171] transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={newImageUrl}
              onChange={e => setNewImageUrl(e.target.value)}
              className="input flex-1 text-xs"
              placeholder={t('form.imageUrl')}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addImage(); } }}
            />
            <button onClick={addImage} className="btn-ghost text-xs flex items-center gap-1">
              <Plus className="w-3 h-3" /> {t('form.addImage')}
            </button>
          </div>
        </div>
      </SectionCard>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
